import {
  IPV6_ADDRESS_MODE_LABELS,
  IPV6_PREFIX_MODE_LABELS,
  describeWanIpv6,
  type Ipv6AddressMode,
  type Ipv6PrefixMode,
  type Ipv6Target,
  type ObservedWanIpv6,
  type OnuVlan,
  type SubscriberWanWriter,
  type WanIpv6,
} from '@simpleacs/shared';
import { IPV6_ADDRESS_MODES, IPV6_PREFIX_MODES, defaultIpv6Intent, defaultIpv6Modes, ipv6ChoiceMatrix, type Ipv6ChoiceMatrix } from './wan-wizard';

/**
 * What `GET …/wan-ipv6` answers: where an IPv6 intent would land, and what
 * the CPE reads today. The WAN mode wizard and the WAN card read it under
 * one query key (`wanIpv6QueryKey`) and run the same two shared pure
 * functions on it — `ipv6Refusals` for what greys a radio, `ipv6WritesFor`
 * for the parameter list — so what either shows is what the server judges.
 */
export interface Ipv6TargetRead {
  target: Ipv6Target;
  connectionPath: string | null;
  observed: ObservedWanIpv6 | null;
  serial: string | null;
}

/**
 * The IPv6 half of the subscriber WAN as PILLS — what the CPE reports, never
 * what the record says. A "dual-stack" record can be applied perfectly and
 * deliver nothing until the upstream offers RA/DHCPv6, so the truth beside
 * the record is the CPE's own protocol, modes and connection status.
 *
 * Nothing is stated that the CPE did not say: a dialect with no IPv6 leaf
 * (the Nokia) yields no pill at all, and the Tenda's numeric mode — whose
 * codes this platform has never seen written — is said to be unverified
 * rather than mapped to the nearest word.
 */
export function observedIpv6Pills(observed: ObservedWanIpv6 | null | undefined): string[] {
  if (!observed) return [];
  if (observed.dialect === 'none') return [];
  if (observed.dialect === 'tdtc') {
    const code = observed.raw.X_TDTC_IPMode;
    return [`IPv6 mode ${code !== undefined ? `code ${code} ` : ''}unverified`];
  }
  const pills: string[] = [];
  if (observed.protocol === 'ipv4') pills.push('IPv4 only');
  if (observed.protocol === 'dual_stack') {
    pills.push('dual stack');
    if (observed.addressMode !== 'unknown') pills.push(`IPv6 ${IPV6_ADDRESS_MODE_LABELS[observed.addressMode]}`);
    if (observed.prefixMode !== 'unknown') pills.push(`prefix ${IPV6_PREFIX_MODE_LABELS[observed.prefixMode]}`);
  }
  if (observed.connStatus) pills.push(`IPv6 ${observed.connStatus}`);
  return pills;
}

/** One line for a summary row: "dual stack · IPv6 Auto · prefix DHCPv6-PD · IPv6 Connected", or null. */
export function observedIpv6Summary(observed: ObservedWanIpv6 | null | undefined): string | null {
  const pills = observedIpv6Pills(observed);
  return pills.length > 0 ? pills.join(' · ') : null;
}

/**
 * The IPv6 half as ONE title-bar pill (operator, 2026-09-14, on
 * ZXICCD53D481: "the onu card is too busy … currently the WAN line shows:
 * 10.2.1.4 · VLAN 5 · DHCP · dual stack · IPv6 Auto · prefix DHCPv6-PD ·
 * IPv6 Connected — reduce to: 10.2.1.4 · VLAN 5 · DHCP — and move the ipv6
 * pill to the title bar"). The ONU card's title bar and the WAN card's title
 * share it, so the two say one thing about one CPE.
 *
 * The pill is the word "IPv6" and nothing more; the full observed words the
 * row used to carry (`observedIpv6Pills`) are its tooltip. Green only when
 * the vendor's own status leaf says Connected — a "dual-stack" record can be
 * applied perfectly and deliver nothing until the upstream offers RA/DHCPv6,
 * so the colour is the CPE's word, never the record's. Null when the CPE
 * reads IPv4-only, unknown, or has no IPv6 leaf at all (a Nokia; a Tenda's
 * numeric mode is never mapped): nothing is stated that the CPE did not say.
 */
export interface Ipv6TitlePill {
  label: 'IPv6';
  tone: 'success' | 'neutral';
  /** "dual stack · IPv6 Auto · prefix DHCPv6-PD · Connected · link-local fe80::…" */
  title: string;
}

export function ipv6TitlePill(observed: ObservedWanIpv6 | null | undefined): Ipv6TitlePill | null {
  if (!observed || observed.protocol !== 'dual_stack') return null;
  if (observed.dialect === 'none' || observed.dialect === 'tdtc') return null;
  const words: string[] = ['dual stack'];
  if (observed.addressMode !== 'unknown') words.push(`IPv6 ${IPV6_ADDRESS_MODE_LABELS[observed.addressMode]}`);
  if (observed.prefixMode !== 'unknown') words.push(`prefix ${IPV6_PREFIX_MODE_LABELS[observed.prefixMode]}`);
  if (observed.connStatus) words.push(observed.connStatus);
  if (observed.linkLocal) words.push(`link-local ${observed.linkLocal}`);
  return {
    label: 'IPv6',
    tone: observed.connStatus === 'Connected' ? 'success' : 'neutral',
    title: words.join(' · '),
  };
}

/**
 * The WAN card's IPv6 group, one word per row (operator, 2026-09-13: "in the
 * configs wan card there is no ipv6 config options") — the same vocabulary as
 * the pills, split into the three rows the SmartOLT dialog asks in: the
 * protocol, the address mode, the prefix-delegation mode. As OBSERVED on the
 * CPE, never the record; null is a dash, because a word the CPE did not say
 * is not shown. A Nokia (no leaf) answers three dashes; a Tenda's numeric
 * mode is said to be unverified in the protocol row and nothing more.
 */
export interface ObservedIpv6Rows {
  protocol: string | null;
  addressMode: string | null;
  prefixMode: string | null;
  /** The WAN link-local address where the model reports one — proof the stack is up. */
  linkLocal: string | null;
  /** The vendor's own connection-status word (`Connected` / `Unconfigured`), read-only. */
  connStatus: string | null;
}

export function observedIpv6Rows(observed: ObservedWanIpv6 | null | undefined): ObservedIpv6Rows {
  const none: ObservedIpv6Rows = { protocol: null, addressMode: null, prefixMode: null, linkLocal: null, connStatus: null };
  if (!observed || observed.dialect === 'none') return none;
  if (observed.dialect === 'tdtc') {
    const code = observed.raw.X_TDTC_IPMode;
    return { ...none, protocol: `mode ${code !== undefined ? `code ${code} ` : ''}unverified`, connStatus: observed.connStatus };
  }
  const dual = observed.protocol === 'dual_stack';
  return {
    protocol: observed.protocol === 'ipv4' ? 'IPv4 only' : dual ? 'Dual stack' : null,
    addressMode: dual && observed.addressMode !== 'unknown' ? IPV6_ADDRESS_MODE_LABELS[observed.addressMode] : null,
    prefixMode: dual && observed.prefixMode !== 'unknown' ? IPV6_PREFIX_MODE_LABELS[observed.prefixMode] : null,
    linkLocal: observed.linkLocal,
    connStatus: observed.connStatus,
  };
}

/**
 * The WAN card's IPv6 DATA group, one row per leaf (operator, 2026-09-14:
 * "the wan card in config doesn't show ipv6 data, just settings — add them
 * just under the ipv4 data and specifically label ipv4 and ipv6"): the
 * address, the delegated prefix, the gateway, the DNS servers, the
 * link-local and the vendor's own status — READ-ONLY, as the CPE reports
 * them, never the record. Null is a dash: the operator has no IPv6 upstream
 * yet, so today every unit reads dashes here, and a leaf a model does not
 * carry (the HG8546M's DNS, the V7.1's link-local) is a dash too rather than
 * a guess. The DNS list is joined the way the IPv4 DNS row reads.
 */
export interface ObservedIpv6Data {
  address: string | null;
  prefix: string | null;
  gateway: string | null;
  dns: string | null;
  linkLocal: string | null;
  connStatus: string | null;
}

export function observedIpv6Data(observed: ObservedWanIpv6 | null | undefined): ObservedIpv6Data {
  const none: ObservedIpv6Data = { address: null, prefix: null, gateway: null, dns: null, linkLocal: null, connStatus: null };
  if (!observed || observed.dialect === 'none') return none;
  return {
    address: observed.address,
    prefix: observed.prefix,
    gateway: observed.gateway,
    dns: observed.dns.length > 0 ? observed.dns.join(', ') : null,
    linkLocal: observed.linkLocal,
    connStatus: observed.connStatus,
  };
}

/**
 * The record beside the observed protocol: "not managed" for the null every
 * row carried before 2026-09-12 (nothing written, nothing compared), else the
 * intent in the shared words — so the card never invents a record the row
 * does not hold.
 */
export function recordedIpv6Label(record: WanIpv6 | null | undefined): string {
  return record ? describeWanIpv6(record) : 'not managed';
}

/**
 * The card's two radio rows (operator, 2026-09-14: "wan card on configs tab
 * should have radio buttons for 2 ipv6 rows: IPv6, IPv6 PD") — the wizard's
 * IP protocol step folded into the visual language of the card's own WAN
 * mode row, without a protocol tile of its own:
 *
 *   IPv6     DHCPv6 | Auto | Static | None — the wizard's address modes.
 *   IPv6 PD  DHCPv6-PD | Static | None — the prefix-delegation modes.
 *
 * There is no "IPv4 only" radio (operator, 2026-09-14, circled it out of the
 * card): None on BOTH rows is IPv4 only — `{ protocol: 'ipv4' }`, IPv6
 * switched off on the CPE — and any other pair is dual stack with those two
 * modes ("None" for the address alone is the wizard's None: no address of
 * its own, a delegated prefix only — the V7.1 fleet default). So the PD row
 * is never greyed as a whole: moving it is how IPv6 is switched off and on.
 *
 * Same vocabulary, same labels, same gate, read PAIRWISE: a radio is greyed
 * with the gate's sentence for the pair it would form with the other row's
 * current choice (`ipv6RowRefusals`), so "None" on the prefix row is open
 * once the address row says None (the pair is IPv4 only, refused only where
 * the target takes no IPv6 choice at all) and greyed beside Auto on a V7.1
 * (a delegated prefix is that firmware's only dual-stack shape). Same
 * defaults: the rows OPEN on the record, else what the CPE reads, else the
 * firmware default (`defaultIpv6Intent` / `defaultIpv6Modes`), never on a
 * refused mode. Null when the target refuses every intent — the radios are
 * then all greyed and the card quotes the sentence, the way the wizard
 * drops the step.
 */
export type Ipv6RowChoice = Ipv6AddressMode;

export const IPV6_ROW_CHOICES: readonly Ipv6RowChoice[] = [...IPV6_ADDRESS_MODES];

export const IPV6_ROW_LABELS: Record<Ipv6RowChoice, string> = { ...IPV6_ADDRESS_MODE_LABELS };

export interface Ipv6Rows {
  address: Ipv6RowChoice;
  prefix: Ipv6PrefixMode;
}

export function defaultIpv6Rows(input: {
  record: WanIpv6 | null | undefined;
  observed: ObservedWanIpv6 | null | undefined;
  refusals: (intent: WanIpv6) => string[];
  /** The matrix when the caller already has it, so it is not derived twice. */
  matrix?: Ipv6ChoiceMatrix;
}): Ipv6Rows | null {
  const matrix = input.matrix ?? ipv6ChoiceMatrix(input.refusals);
  const intent = defaultIpv6Intent(input);
  const modes = defaultIpv6Modes({ ...input, matrix });
  if (!intent || !modes) return null;
  return intent.protocol === 'ipv4'
    ? { address: 'none', prefix: 'none' }
    : { address: intent.addressMode, prefix: intent.prefixMode };
}

/**
 * The card's IPv6 SWITCH and what it shows (operator, 2026-10-21: "hide
 * options not supported in the different config cards, until we add them —
 * on IPv6 hide static if not supported, but have a switch to enable/disable
 * IPv6"). Off is the pair (None, None) — IPv4 only, the protocol switched off
 * on the CPE; on is the first pair this unit can take, in the platform's
 * order (`defaultIpv6Modes`: the record, then what the CPE reads, then Auto +
 * DHCPv6-PD). A unit with no writable dual-stack pair (the Tenda HG10c the day
 * only None was proven) cannot be switched on, and the switch says so with
 * the gate's own sentence rather than offering a greyed row of radios.
 */
export const ipv6IsOn = (rows: Ipv6Rows | null): boolean => rows !== null && !(rows.address === 'none' && rows.prefix === 'none');

export function ipv6SwitchOnPair(input: {
  record: WanIpv6 | null | undefined;
  observed: ObservedWanIpv6 | null | undefined;
  refusals: (intent: WanIpv6) => string[];
  matrix?: Ipv6ChoiceMatrix;
}): Ipv6Rows | null {
  const modes = defaultIpv6Modes(input);
  return modes ? { address: modes.addressMode, prefix: modes.prefixMode } : null;
}

/**
 * Why the switch cannot turn IPv6 on here, in the gate's words: null while
 * some dual-stack pair in the platform's order is writable; else the
 * wholesale refusal, else the first sentence against Auto / DHCPv6 on the
 * address or DHCPv6-PD on the prefix (Static is never the switch's target).
 */
export function ipv6SwitchOffReason(matrix: Ipv6ChoiceMatrix): string | null {
  if (matrix.wholesale.length > 0) return matrix.wholesale[0]!;
  const addressOk = (['auto', 'dhcpv6', 'none'] as const).find((m) => matrix.address[m].length === 0) ?? null;
  const prefixOk = (['dhcpv6_pd', 'none'] as const).find((m) => matrix.prefix[m].length === 0) ?? null;
  if (addressOk && prefixOk && !(addressOk === 'none' && prefixOk === 'none')) return null;
  const sentences = [...matrix.address.auto, ...matrix.address.dhcpv6, ...matrix.prefix.dhcpv6_pd];
  return sentences[0] ?? null;
}

/**
 * The radios the card SHOWS: a mode refused with every pairing is hidden
 * (until the platform learns its word), the row's current choice and the
 * recorded one stay visible whatever the matrix says — a record this unit
 * cannot carry is still a fact to see, greyed with its sentence.
 */
export function visibleIpv6Choices(
  matrix: Ipv6ChoiceMatrix,
  rows: Ipv6Rows | null,
  record: WanIpv6 | null | undefined,
): { address: Ipv6RowChoice[]; prefix: Ipv6PrefixMode[] } {
  const recordedAddress = record?.protocol === 'dual_stack' ? record.addressMode : null;
  const recordedPrefix = record?.protocol === 'dual_stack' ? record.prefixMode : null;
  return {
    address: IPV6_ROW_CHOICES.filter((c) => matrix.address[c].length === 0 || rows?.address === c || recordedAddress === c),
    prefix: IPV6_PREFIX_MODES.filter((p) => matrix.prefix[p].length === 0 || rows?.prefix === p || recordedPrefix === p),
  };
}

/** The intent the two rows spell: None on both is the ipv4 protocol, anything else dual stack with both modes. */
export function ipv6IntentFromRows(rows: Ipv6Rows): WanIpv6 {
  return rows.address === 'none' && rows.prefix === 'none'
    ? { protocol: 'ipv4' }
    : { protocol: 'dual_stack', addressMode: rows.address, prefixMode: rows.prefix };
}

/**
 * The gate's sentences for one radio — what greys it, and its title — for
 * the pair it would form with the OTHER row as it stands: an address radio
 * beside the current prefix, a prefix radio beside the current address.
 * Judged by the gate itself rather than the per-radio matrix, because the
 * pair (None, None) is a different intent (IPv4 only) from either None alone.
 */
export function ipv6RowRefusals(
  judge: (intent: WanIpv6) => string[],
  rows: Ipv6Rows,
  radio: { address: Ipv6RowChoice } | { prefix: Ipv6PrefixMode },
): string[] {
  const pair: Ipv6Rows = 'address' in radio ? { ...rows, address: radio.address } : { ...rows, prefix: radio.prefix };
  return judge(ipv6IntentFromRows(pair));
}

/**
 * What an IPv6 intent does to the VLAN row's record — the WAN mode wizard's
 * Review line and the WAN card's Save hint, in one place so the two say the
 * same thing about the same PATCH: recorded where the row held nothing,
 * re-stated where it already holds this, replacing the record otherwise.
 * Compared by the shared words, as the wizard always compared them.
 */
export type Ipv6RecordRelation = { kind: 'recorded' | 'same' | 'replaces'; text: string };

export function ipv6RecordRelation(intent: WanIpv6, record: WanIpv6 | null | undefined): Ipv6RecordRelation {
  if (!record) return { kind: 'recorded', text: 'recorded on the VLAN row (it recorded nothing before)' };
  if (describeWanIpv6(record) === describeWanIpv6(intent)) return { kind: 'same', text: 're-stated; the row already records it' };
  return { kind: 'replaces', text: `replaces the record (${describeWanIpv6(record)})` };
}

/**
 * The IPv6 bar's own state — whether a CLEAN pair of rows still has
 * something to send, and the sentence beside the button.
 *
 * The rows open on the record only where this CPE can carry it
 * (`defaultIpv6Rows` never lands on a refused radio), so the exact cases the
 * wizard was built for used to be un-saveable from the card (review,
 * 2026-09-14): a row recording DHCPv6 on an F670L V7.1 opens on Auto with
 * the DHCPv6 radio greyed and marked "recorded" — the wizard's Apply records
 * Auto in its place, but on the card Auto was the default, nothing was
 * dirty and Apply stayed dead; a row recording nothing opens on what the
 * CPE reads, and nothing let the platform MANAGE that intent from here; a
 * record the CPE has drifted from could not be re-pushed, where the IPv4
 * half has Re-apply for exactly that. So the bar is judged against the
 * RECORD, not the default: a moved radio is Apply; a clean pair that differs
 * from the record — or, on a ZTE chassis, equals it and can be resent to
 * the CPE — is Re-apply. On a Huawei chassis a record re-sent goes nowhere
 * (no CPE half is written from here), so the bar stays dead for it.
 */
export function ipv6SaveBarState(input: {
  intent: WanIpv6 | null;
  record: WanIpv6 | null | undefined;
  dirty: boolean;
  zte: boolean;
  /**
   * Which writer the omci-wan call lands on, from the dry run — the chassis
   * wan-ip line, or the CPE's own connection over TR-069 (the 2026-09-14
   * duplicate-WAN rule). Null while unread: the hint then names the IPv4
   * WAN and not a line the unit may not have.
   */
  writer?: SubscriberWanWriter | null;
}): { resendable: boolean; hint: string | null } {
  if (!input.intent) return { resendable: false, hint: null };
  const relation = ipv6RecordRelation(input.intent, input.record);
  const sentence = relation.text.charAt(0).toUpperCase() + relation.text.slice(1);
  if (input.dirty) return { resendable: false, hint: `${sentence} — runs in the tasks tray` };
  // A moved radio gets the drop said under the rows (`wanApplyDisclosure`);
  // a clean card does not, so the Re-apply hint carries it on a ZTE chassis —
  // naming the object the writer re-states, never assuming the chassis line.
  const object =
    input.writer === 'zte_chassis'
      ? 'the chassis wan-ip line'
      : input.writer === 'zte_tr069' || input.writer === 'huawei_ont'
        ? "the CPE's own WAN connection (over TR-069)"
        : 'the IPv4 WAN';
  const drop = input.zte ? `; ${object} is re-stated with it, a brief drop` : '';
  switch (relation.kind) {
    case 'recorded':
      return {
        resendable: true,
        hint: `The rows opened on what the CPE reads, or ships with — Re-apply records it on the VLAN row (it recorded nothing before), so the platform manages it from here${drop}`,
      };
    case 'replaces':
      return {
        resendable: true,
        hint: `The rows opened on ${describeWanIpv6(input.intent)} because the record (${describeWanIpv6(input.record)}) is one this CPE cannot carry — Re-apply replaces the record with it${drop}`,
      };
    case 'same':
      return input.zte
        ? {
            resendable: true,
            hint: `Resends the record to the CPE (${object} is re-stated — a brief drop) — use if a write never reached it`,
          }
        : { resendable: false, hint: null };
  }
}

/**
 * The VLAN row whose IPv6 record describes the subscriber WAN. The one on the
 * VLAN the CPE reports its WAN on where the platform holds it, else the first
 * subscriber row — the WAN mode dialog's own default (`attached[0]`), so the
 * card and the dialog read the same record and key the same `wan-ipv6` query
 * (its mode is this row's). Management and VoIP rows are never the WAN.
 */
export function subscriberWanRow(rows: OnuVlan[], wanVlanId: string | null | undefined): OnuVlan | null {
  const subscriber = rows.filter((row) => row.purpose !== 'management' && row.purpose !== 'voip');
  const onWanVlan = wanVlanId ? subscriber.find((row) => String(row.vlanId) === wanVlanId) : undefined;
  return onWanVlan ?? subscriber[0] ?? null;
}

/**
 * The query the WAN card and the WAN mode dialog share for `GET …/wan-ipv6`
 * — the dialog keys its read `['onu-config', base, 'wan-ipv6', mode]`
 * (WanModeDialog.tsx, not touched from here), and the card must match it
 * byte for byte or the two hold separate answers about one CPE. `base` is
 * the ONU page's `configBase` (`/devices/<id>` for a linked device).
 */
export function wanIpv6QueryKey(base: string, mode: 'route' | 'bridge'): string[] {
  return ['onu-config', base, 'wan-ipv6', mode];
}

/**
 * The prefix that invalidates that read in BOTH modes — what the WAN panel's
 * refresh and the ONU page's 15 s loop hand to react-query. The card's rows
 * used to freeze at the first answer (review, 2026-09-13): the loop re-read
 * `['device', id]` and the VLAN rows, so External IP kept moving while "IP
 * protocol / IPv6 address" showed whatever the CPE said when the panel
 * opened, until it was closed and opened again.
 */
export function wanIpv6QueryScope(base: string): string[] {
  return ['onu-config', base, 'wan-ipv6'];
}
